import { useState } from 'react';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import KeyboardBackspaceOutlinedIcon from '@mui/icons-material/KeyboardBackspaceOutlined';
import RuleRoundedIcon from '@mui/icons-material/RuleRounded';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import { CustomRuleList } from './CustomRuleList';
import { CustomRuleDetails } from './CustomRuleDetail';
import { CustomRuleListItem } from 'types/api/Tenant/AROKMS/CustomRuleTypes';

const ModalContentStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 980,
  maxHeight: '88vh',
  bgcolor: 'background.paper',
  borderRadius: 2,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

const headerStyle = {
  px: 3,
  pt: 2.5,
  pb: 2,
  bgcolor: '#F8FAFC',
  borderBottom: '1px solid #E3EBF6',
};

const contentStyle = {
  flex: 1,
  overflow: 'auto',
  position: 'relative' as const,
};

const closeIconStyle = { color: '#98A2AE', cursor: 'pointer' };
const headerIconStyle = { color: '#3B4797', fontSize: 28 };

const backButtonStyles = {
  textTransform: 'none',
  p: 0,
  minWidth: 0,
  fontSize: 13,
  color: '#667085',
  fontWeight: 600,
  '&:hover': {
    color: '#3B4797',
    bgcolor: 'transparent',
  },
};

const MODAL_VIEW_STATE_CUSTOM_RULE = {
  CUSTOM_RULE_LIST: 'CUSTOM_RULE_LIST',
  CUSTOM_RULE_DETAIL: 'CUSTOM_RULE_DETAIL',
};

const transitionStyles = {
  entering: { transform: 'translateX(-100%)', opacity: 0, position: 'absolute' } as React.CSSProperties,
  entered: {
    transform: 'translateX(0)',
    opacity: 1,
    transition: 'transform 0.3s, opacity 0.3s',
  } as React.CSSProperties,
  exiting: {
    transform: 'translateX(100%)',
    opacity: 0,
    position: 'absolute',
  } as React.CSSProperties,
  exited: { transform: 'translateX(0)', opacity: 1, transition: 'transform 0.3s, opacity 0.3s' } as React.CSSProperties,
};

export interface ModalDataInputCustomRuleProps {
  visible?: boolean;
  onClose?: () => void;
  subjectId?: string | number;
}

export function ModalDataInputCustomRule(props: ModalDataInputCustomRuleProps) {
  const { onClose, visible = false, subjectId } = props;
  const [modalView, setModalView] = useState(MODAL_VIEW_STATE_CUSTOM_RULE.CUSTOM_RULE_LIST);
  const [customRuleEditItem, setCustomRuleEditItem] = useState<CustomRuleListItem | null>(null);

  const isShowList = modalView === MODAL_VIEW_STATE_CUSTOM_RULE.CUSTOM_RULE_LIST;
  const isShowDetail = modalView === MODAL_VIEW_STATE_CUSTOM_RULE.CUSTOM_RULE_DETAIL;
  const isEditing = Boolean(customRuleEditItem);

  const handleOnCloseModal = () => {
    setModalView(MODAL_VIEW_STATE_CUSTOM_RULE.CUSTOM_RULE_LIST);
    setCustomRuleEditItem(null);
    onClose?.();
  };

  const handleOnCreateCustomRule = () => {
    setCustomRuleEditItem(null);
    setModalView(MODAL_VIEW_STATE_CUSTOM_RULE.CUSTOM_RULE_DETAIL);
  };

  const handleOnEditCustomRule = (data: CustomRuleListItem) => {
    setModalView(MODAL_VIEW_STATE_CUSTOM_RULE.CUSTOM_RULE_DETAIL);
    setCustomRuleEditItem(data);
  };

  const handleOnClickBackToList = () => {
    setModalView(MODAL_VIEW_STATE_CUSTOM_RULE.CUSTOM_RULE_LIST);
    setCustomRuleEditItem(null);
  };

  const getHeaderTitle = () => {
    if (isShowList) return 'Custom Rules';
    if (isEditing) return 'Edit Custom Rule';
    return 'Create Custom Rule';
  };

  const getHeaderSubtitle = () => {
    if (isShowList) return 'Manage rules that control attribute visibility, naming, and formatting.';
    if (isEditing) return `Editing rule for this attribute.`;
    return 'Define conditions and actions for the new rule.';
  };

  return (
    <Modal open={visible} onClose={handleOnCloseModal}>
      <Stack direction='column' sx={ModalContentStyle}>
        {/* ── Header ── */}
        <Box sx={headerStyle}>
          <Stack direction='row' justifyContent='space-between' alignItems='flex-start'>
            <Stack direction='row' spacing={1.5} alignItems='center'>
              {isShowDetail && (
                <Button
                  variant='text'
                  disableRipple
                  onClick={handleOnClickBackToList}
                  sx={backButtonStyles}
                  startIcon={<KeyboardBackspaceOutlinedIcon sx={{ fontSize: '20px !important' }} />}
                >
                  Back
                </Button>
              )}
              {isShowDetail && (
                <Divider orientation='vertical' flexItem sx={{ mx: 1, borderColor: '#E3EBF6' }} />
              )}
              <RuleRoundedIcon sx={headerIconStyle} />
              <Stack>
                <Typography variant='modal-title' component='h2'>
                  {getHeaderTitle()}
                </Typography>
                <Typography variant='input-label-gray' fontSize={13}>
                  {getHeaderSubtitle()}
                </Typography>
              </Stack>
            </Stack>
            <IconButton onClick={handleOnCloseModal} size='small'>
              <HighlightOffRoundedIcon sx={closeIconStyle} />
            </IconButton>
          </Stack>
        </Box>

        {/* ── Content ── */}
        <Box sx={contentStyle}>
          <CustomRuleList
            onCreateCustomRule={handleOnCreateCustomRule}
            onEditCustomRule={handleOnEditCustomRule}
            subjectId={subjectId}
            style={isShowList ? transitionStyles.entered : transitionStyles.exiting}
          />
          <CustomRuleDetails
            onBackToList={handleOnClickBackToList}
            editRow={customRuleEditItem}
            style={isShowList ? transitionStyles.entering : transitionStyles.entered}
            subjectId={subjectId}
          />
        </Box>
      </Stack>
    </Modal>
  );
}
